import {
  apiGet, apiPost,
} from '../../_shared/utils/api/api.helpers';
import { type MetricsDataAction } from '../../_shared/utils/metric/metrics.types';
import { type BoundaryFilterRequest } from '../../spreadsheet/filter/boundary/spreadsheetFilterBoundary.types';
import { type BoundaryTerritoryFilterRequest } from '../../spreadsheet/filter/boundaryTerritory/spreadsheetFilterBoundaryTerritory.types';
import { type PolygonFilterRequest } from '../../spreadsheet/filter/polygon/spreadsheetPolygonFilter.types';
import { type RadiusFilterRequest } from '../../spreadsheet/filter/radius/spreadsheetFilterRadius.types';
import {
  type BoundaryGroupDemographic, type BoundaryGroupDemographicServerModel,
} from '../boundaryGroups/boundaryGroups.repository';
import { createDemographicsResultFromResponse } from './demographics.factory';
import type { DemographicRegion } from './demographics.types';

export type DemographicsGlobalListResponse = {
  regions: DemographicsRegionResponse[];
};

export type DemographicsPremiumListResponse = Readonly<{
  data: DemographicsRegionResponse[];
}>;

export type DemographicsRegionResponse = {
  id: DemographicRegion;
  demographics: BoundaryGroupDemographicServerModel[];
};

export type DemographicsRegion = {
  id: DemographicRegion;
  demographics: readonly BoundaryGroupDemographic[];
};

export type DemographicsQueryRequest = {
  map_id: number;
  demographics_ids: ReadonlyArray<string>;
  boundary_group_id?: number;
  boundary_territory_filter?: BoundaryTerritoryFilterRequest;
  boundary_filter?: BoundaryFilterRequest;
  radius_filter?: RadiusFilterRequest;
  polygon_filter?: PolygonFilterRequest;
};

type DemographicsQueryBulkRequest = {
  queries: DemographicsQueryBulkRequestItem[];
};

export type DemographicsQueryBulkResults = Record<DemographicQueryBulkKey, {
  isSuccess: boolean;
  data: ReadonlyArray<DemographicsResult>;
}>;

type DemographicQueryBulkKey = string;

type DemographicsQueryBulkRequestItem = DemographicsQueryRequest & {
  id: DemographicQueryBulkKey;
};

export type DemographicsResultResponse = {
  category: string;
  function_used: MetricsDataAction;
  name: string;
  percentage: boolean;
  value: number;
  value_of_total?: number;
  pre_data: string;
  post_data: string;
} & ({
  demographics_id: number; // old model
  codename: string;
} | {
  demographics_id: string; // new model
  codename: undefined;
});

export type DemographicsResult = {
  demographicId: string;
  category: string;
  functionUsed: MetricsDataAction;
  name: string;
  percentage: boolean;
  value: number;
  valueOfTotal?: number;
  preData: string;
  postData: string;
};

export const getDemographicsListGlobal = (clientId: number): Promise<DemographicsGlobalListResponse> => {
  const requestUrl = `/api/clients/${clientId}/demographics/list-global`;

  return apiGet(requestUrl);
};

export const getDemographicsListAgs = (clientId: number): Promise<DemographicsPremiumListResponse> => {
  const requestUrl = `/api/clients/${clientId}/demographics/list-ags`;

  return apiGet(requestUrl);
};

export const getDemographicsQuery = (clientId: number, request: DemographicsQueryRequest): Promise<ReadonlyArray<DemographicsResult>> => {
  const requestUrl = `/api/clients/${clientId}/demographics/query`;

  return apiPost<{ data: ReadonlyArray<DemographicsResultResponse> }>(requestUrl, request)
    .then(response => response.data.map(item => createDemographicsResultFromResponse(item)));
};

export const getDemographicsQueryBulk = (clientId: number, request: DemographicsQueryBulkRequest): Promise<DemographicsQueryBulkResults> => {
  const requestUrl = `/api/clients/${clientId}/demographics/query/bulk`;

  return apiPost<{ data: Record<DemographicQueryBulkKey, { success: boolean; data: DemographicsResultResponse[] }> }>(requestUrl, request)
    .then(response => {
      return Object.fromEntries(
        Object.entries(response.data).map(([queryId, value]) => [
          queryId,
          {
            isSuccess: value.success,
            data: value.data.map(item => createDemographicsResultFromResponse(item)),
          },
        ])
      );
    });
};
